<template>
    <div class="env-page" v-if="ready">
        <div class="env-container fade-in">

            <div class="env-header">
                <img src="/img/logo.png" alt="Logo" class="env-logo" />
                <div class="env-greeting" v-if="claims">
                    {{ $t('hello') }}, <span class="env-user-name">{{ claims.Entity.name }}</span>
                </div>
                <h2 class="env-title">{{ $t('chooseYourEnvironment') }}</h2>
                <p class="env-subtitle">{{ $t('environmentPageSubtitle') }}</p>
            </div>

            <div class="env-grid">
                <div
                    class="env-card"
                    v-for="(project, index) in projects"
                    :key="project.uuid"
                    :style="{ animationDelay: (index * 0.06) + 's' }"
                    @click="choosen(project)"
                >
                    <div class="env-card-icon-wrap" :class="'env-icon-' + (index % 4)">
                        <span class="env-card-initial">{{ project.name.charAt(0).toUpperCase() }}</span>
                    </div>
                    <div class="env-card-name">{{ project.name }}</div>
                    <div class="env-card-arrow">
                        <i class="fa fa-arrow-right"></i>
                    </div>
                </div>
            </div>

            <div class="env-footer">
                <button class="env-logout-btn" @click="logout">
                    <i class="fa fa-sign-out"></i> {{ $t('logout') }}
                </button>
            </div>

        </div>

        <ReLogin v-model="showLogin" :project_uuid="loginTo" @success="successLogin"/>
    </div>
</template>

<style scoped lang="scss">
.env-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--bg-main-start) 0%, var(--bg-main-end) 100%);
    padding: 2rem 1rem;
}

.env-container {
    width: 100%;
    max-width: 960px;
}

/* Header */
.env-header {
    text-align: center;
    margin-bottom: 2.5rem;
}

.env-logo {
    max-width: 52px;
    height: auto;
    margin-bottom: 1.5rem;
}

.env-greeting {
    font-size: 0.9rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    letter-spacing: 0.02em;
}

.env-user-name {
    color: var(--text-primary);
    font-weight: 600;
}

.env-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
    letter-spacing: -0.02em;
}

.env-subtitle {
    font-size: 0.9rem;
    color: var(--text-muted);
    margin: 0;
}

/* Grid */
.env-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
}

@media (max-width: 768px) {
    .env-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 440px) {
    .env-grid {
        grid-template-columns: 1fr;
    }
}

/* Card */
@keyframes cardIn {
    from {
        opacity: 0;
        transform: translateY(12px) scale(0.97);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.env-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.75rem;
    background-color: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-2xl);
    padding: 1.75rem 1.25rem 1.5rem;
    cursor: pointer;
    transition: var(--transition-default);
    box-shadow: var(--shadow-sm);
    position: relative;
    animation: cardIn 0.4s ease both;
}

.env-card:hover {
    border-color: var(--border-active);
    background-color: var(--bg-card-secondary);
    box-shadow: var(--shadow-primary-hover);
    transform: translateY(-4px);
}

.env-card:active {
    transform: translateY(0);
    box-shadow: var(--shadow-sm);
}

/* Icon circle with initial */
.env-card-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: var(--radius-xl);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: var(--transition-default);
}

.env-icon-0 {
    background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
    box-shadow: var(--shadow-primary);
}

.env-icon-1 {
    background: linear-gradient(135deg, var(--success-start), var(--success-end));
    box-shadow: var(--shadow-success);
}

.env-icon-2 {
    background: linear-gradient(135deg, var(--color-info), #6c80ff);
    box-shadow: 0 4px 15px rgba(66, 153, 225, 0.3);
}

.env-icon-3 {
    background: linear-gradient(135deg, var(--color-warning), #f6ad55);
    box-shadow: 0 4px 15px rgba(237, 137, 54, 0.3);
}

.env-card-initial {
    font-size: 1.25rem;
    font-weight: 700;
    color: #fff;
    line-height: 1;
    text-transform: uppercase;
}

.env-card-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary);
    word-break: break-word;
}

/* Arrow indicator */
.env-card-arrow {
    position: absolute;
    top: 0.75rem;
    right: 0.85rem;
    font-size: 0.75rem;
    color: var(--text-disabled);
    opacity: 0;
    transform: translate(-4px, 4px);
    transition: var(--transition-quick);
}

.env-card:hover .env-card-arrow {
    opacity: 1;
    transform: translate(0, 0);
    color: var(--primary-start);
}

/* Footer */
.env-footer {
    display: flex;
    justify-content: center;
    margin-top: 2.5rem;
}

.env-logout-btn {
    background: none;
    border: 1px solid var(--border-default);
    color: var(--text-muted);
    font-size: 0.85rem;
    padding: 0.5rem 1.25rem;
    border-radius: var(--radius-lg);
    cursor: pointer;
    transition: var(--transition-default);
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.env-logout-btn:hover {
    color: var(--color-error);
    border-color: var(--color-error);
    background-color: rgba(252, 129, 129, 0.08);
}

</style>

<script>
import ReLogin from '../users/ReLogin'

export default {
    components: {
        ReLogin
    },

    data: function() {
        return {
            showLogin: false,
            loginTo: null,
            ready: false
        }
    },

    methods: {
        choosen(project) {
            this.loginTo = project.uuid
            this.showLogin = true
        },

        successLogin() {
            localStorage.setItem("current_project_uuid", this.loginTo)
            this.$router.push({query: {}})
            this.$router.go("/home")
        },

        logout() {
            this.$http.post("/v1/user/logout").then(function() {
                this.$router.push('/login')
            }.bind(this)).catch(function() {
                this.$router.push('/login')
            }.bind(this))
        },
    },

    computed: {
        projects() {
            return this.$store.getters.projects
        },

        claims() {
            return this.$store.getters.getClaims
        },

        currentProject() {
            return this.$store.getters.getCurrentProject
        },

        force() {
            return this.$route.query.force
        }
    },

    async created() {
        try {
            await this.$store.dispatch('fetchClaims')
            await this.$store.dispatch('fetchProjects')

            if (this.force) {
                this.ready = true
                return
            }

            if (!this.currentProject) {
                this.ready = true
            } else {
                this.$router.push("/home")
            }

        } catch (e) {
            console.error(e)
            if (e.response && e.response.status == 401) {
                this.$store.commit('setClaims', null)
                this.$router.push("/login")
            }
        }
    }
}
</script>